import React from 'react';
import {FiX, FiChevronLeft, FiChevronRight, FiEye, FiEyeOff, FiPlusCircle} from 'react-icons/fi';
import {useDocumentSpotlight, SpotlightItem} from '../../../../contexts/document_spotlight_context';
import {useWatchListContext} from '../../../../contexts/watch_list_context';
import {PERSISTENT_WATCH_PHRASE_HIGHLIGHTING, TEMPORARY_WATCH_PHRASE_HIGHLIGHTING} from '../../../../feature_flags';

interface SpotlightBarProps {
    onPrev?: () => void;
    onNext?: () => void;
}

const SpotlightRow: React.FC<{
    item: SpotlightItem;
    isActive: boolean;
    activeMatchInfo: {currentMatch: number; totalMatches: number};
    onActivate: () => void;
    onToggleVisibility: () => void;
    onRemove: () => void;
    onBookmark?: () => void;
    onPrev?: () => void;
    onNext?: () => void;
}> = ({item, isActive, activeMatchInfo, onActivate, onToggleVisibility, onRemove, onBookmark, onPrev, onNext}) => {
    const truncated = item.text.length > 60 ? item.text.slice(0, 57) + '\u2026' : item.text;

    return (
        <div
            className={`flex items-center gap-1.5 px-3 py-1 text-xs ${
                item.isSync
                    ? 'bg-blue-50/50 border-b border-blue-100 opacity-60'
                    : isActive ? 'bg-amber-50 border-b border-amber-200' : 'bg-slate-50 border-b border-slate-200'
            }`}
            style={item.color ? {borderLeftColor: item.color, borderLeftWidth: 3}
                : item.isSync ? {borderLeftColor: '#93c5fd', borderLeftWidth: 3} : undefined}
        >
            {/* Source badge */}
            <span className={`shrink-0 rounded px-1.5 py-0.5 font-medium ${
                item.isSync ? 'bg-blue-100/60 text-blue-400'
                    : isActive ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'
            }`}>
                {item.isSync ? 'Sync' : item.source}
            </span>

            {/* Page hint */}
            {item.pageHint != null && (
                <span className="shrink-0 tabular-nums text-[10px] text-slate-400 font-medium">
                    p.{item.pageHint}
                </span>
            )}

            {/* Quote text — click to activate & navigate */}
            <button
                type="button"
                onClick={onActivate}
                className={`truncate min-w-0 italic text-left hover:underline ${
                    isActive ? 'text-slate-700' : 'text-slate-500'
                } ${!item.visible ? 'line-through opacity-50' : ''}`}
                title={`Click to jump to: "${item.text}"`}
            >
                &ldquo;{truncated}&rdquo;
            </button>

            {/* Match scrubber — only for active item */}
            {isActive && activeMatchInfo.totalMatches > 0 && (
                <div className="flex items-center gap-0.5 shrink-0 ml-auto">
                    <button
                        onClick={onPrev}
                        disabled={activeMatchInfo.totalMatches <= 1}
                        className="p-0.5 rounded hover:bg-amber-100 disabled:opacity-30 transition-colors"
                        title="Previous match"
                    >
                        <FiChevronLeft className="w-3.5 h-3.5"/>
                    </button>
                    <span className="tabular-nums text-slate-500 px-0.5">
                        {activeMatchInfo.currentMatch + 1}/{activeMatchInfo.totalMatches}
                    </span>
                    <button
                        onClick={onNext}
                        disabled={activeMatchInfo.totalMatches <= 1}
                        className="p-0.5 rounded hover:bg-amber-100 disabled:opacity-30 transition-colors"
                        title="Next match"
                    >
                        <FiChevronRight className="w-3.5 h-3.5"/>
                    </button>
                </div>
            )}

            {isActive && activeMatchInfo.totalMatches === 0 && (
                <span className="shrink-0 ml-auto text-slate-400">No matches found</span>
            )}

            {!isActive && <div className="flex-1"/>}

            {/* Visibility toggle */}
            <button
                onClick={onToggleVisibility}
                className={`p-1 rounded transition-colors shrink-0 ${
                    item.visible
                        ? 'hover:bg-amber-100 text-slate-500'
                        : 'hover:bg-slate-200 text-slate-300'
                }`}
                title={item.visible ? 'Hide highlight' : 'Show highlight'}
            >
                {item.visible ? <FiEye className="w-3.5 h-3.5"/> : <FiEyeOff className="w-3.5 h-3.5"/>}
            </button>

            {/* Add to watch list — hidden when watch list feature is off */}
            {onBookmark && (
                <button
                    onClick={onBookmark}
                    className="p-1 rounded hover:bg-amber-100 text-amber-600 transition-colors shrink-0"
                    title="Add to watch list highlights"
                >
                    <FiPlusCircle className="w-3.5 h-3.5"/>
                </button>
            )}

            {/* Dismiss */}
            <button
                onClick={onRemove}
                className="p-1 rounded hover:bg-amber-100 text-slate-400 hover:text-slate-600 transition-colors shrink-0"
                title="Remove spotlight"
            >
                <FiX className="w-3.5 h-3.5"/>
            </button>
        </div>
    );
};

const showWatchListBookmark = PERSISTENT_WATCH_PHRASE_HIGHLIGHTING || TEMPORARY_WATCH_PHRASE_HIGHLIGHTING;

const SpotlightBar: React.FC<SpotlightBarProps> = ({onPrev, onNext}) => {
    const ctx = useDocumentSpotlight();
    const watchCtx = useWatchListContext();
    if (!ctx || ctx.spotlights.length === 0) return null;

    const {spotlights, activeSpotlightId, activeMatchInfo, jumpToSpotlight, removeSpotlight, toggleSpotlightVisibility, clearAllSpotlights} = ctx;

    // Sort by page (known pages first, then unknown in insertion order)
    const sorted = [...spotlights].sort((a, b) => {
        const ap = a.pageHint;
        const bp = b.pageHint;
        if (ap == null && bp == null) return 0;
        if (ap == null) return 1;
        if (bp == null) return -1;
        return ap - bp;
    });

    // Cap the bar height once there are many items; individual rows scroll internally.
    const scrollThreshold = 4;
    const isScrolling = sorted.length > scrollThreshold;

    return (
        <div className="border-b border-slate-200">
            {sorted.length > 0 && (
                <div className="flex items-center justify-between px-3 py-1 text-[10px] uppercase tracking-wide text-slate-400 bg-slate-100/60">
                    <span>{sorted.length} highlights{isScrolling ? ' · sorted by page' : ''}</span>
                    <button
                        onClick={clearAllSpotlights}
                        className="text-slate-400 hover:text-slate-700 transition-colors"
                        title="Clear all highlights"
                    >
                        Clear all
                    </button>
                </div>
            )}
            <div className={isScrolling ? 'max-h-[132px] overflow-y-auto' : ''}>
                {sorted.map(item => (
                    <SpotlightRow
                        key={item.id}
                        item={item}
                        isActive={item.id === activeSpotlightId}
                        activeMatchInfo={activeMatchInfo}
                        onActivate={() => jumpToSpotlight({text: item.text, source: item.source, color: item.color, pageHint: item.pageHint})}
                        onToggleVisibility={() => toggleSpotlightVisibility(item.id)}
                        onRemove={() => removeSpotlight(item.id)}
                        onBookmark={showWatchListBookmark ? () => watchCtx?.addTemporaryWatchPhrase(item.text) : undefined}
                        onPrev={onPrev}
                        onNext={onNext}
                    />
                ))}
            </div>
        </div>
    );
};

export default SpotlightBar;
